// New-password entry with the live policy checklist. Shared by the forced
// rotation screen (/change-password), the one-time account-setup screen,
// and the admin Users modals (create user, reset password).
//
// Two things the props exist for:
//   • `policy` — helpers/passwordPolicy: "strong" (Manager+, 14+, every
//     class) or "memorable" (9+, a letter and a digit; capitals/symbols
//     optional). The checklist shows only the rules the policy requires,
//     and the generator button — in the new-password field's label row, next
//     to the field it fills (Jack, 2026-10-20) — matches: a random strong
//     string, or an adjective + noun + number in the UI language that
//     regenerates on every press until the user likes one.
//   • `autoCompleteMode` — "new-password" on the self-service screens so the
//     browser offers to SAVE the credential (that is what lets Face ID /
//     fingerprint / Windows Hello fill it next time); "off" in the admin
//     modals, with a non-"password" field name, so an admin's own password
//     manager never captures a password they typed for someone else.
//   • `allowSave` — the self-service screens pass the Settings › Login screen
//     switch (2026-10-29, components/login/savedLogins). Off — the default
//     there — renders fields the browser does not take for passwords at all
//     (SecretInput), so it never offers to save the new one. The admin
//     modals leave it at true and keep the plain field above.

import { useId, useState } from "react";
import { Check, X, Eye, EyeOff, Wand2, Dices } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "../i18n";
import { evaluatePolicy, generatePasswordFor, type PasswordContext, type PasswordPolicy } from "./helpers/passwordPolicy";
import SecretInput from "./login/SecretInput";
import "../styles/change-password.css";

export interface PasswordFieldsProps {
    newPassword: string;
    onNewPasswordChange: (value: string) => void;
    confirmPassword?: string;
    onConfirmPasswordChange?: (value: string) => void;
    policy: PasswordPolicy;
    /** The account the password is for — its username / name must not appear in it. */
    context?: PasswordContext;
    /** Render the confirm field. Default true; admin modals pass false (the admin reads the value). */
    requireConfirm?: boolean;
    /** Show the generate / suggest button. */
    showGenerator?: boolean;
    autoCompleteMode?: "new-password" | "off";
    /** May the browser save / fill these fields? Self-service screens pass the Login screen switch. */
    allowSave?: boolean;
    /** Start with the text visible (admin modals: the admin has to read it out). */
    defaultRevealed?: boolean;
    /** "dark" on the auth screens, "light" inside white modal cards. */
    variant?: "dark" | "light";
    disabled?: boolean;
    autoFocus?: boolean;
    newLabelKey?: string;
    confirmLabelKey?: string;
}

const PasswordFields = ({
    newPassword,
    onNewPasswordChange,
    confirmPassword = "",
    onConfirmPasswordChange,
    policy,
    context = {},
    requireConfirm = true,
    showGenerator = false,
    autoCompleteMode = "new-password",
    allowSave = true,
    defaultRevealed = false,
    variant = "dark",
    disabled = false,
    autoFocus = false,
    newLabelKey = "change_password_new_label",
    confirmLabelKey = "change_password_confirm_label",
}: PasswordFieldsProps) => {
    const { t, language } = useTranslation();
    const [showNew, setShowNew] = useState(defaultRevealed);
    const [showConfirm, setShowConfirm] = useState(defaultRevealed);
    const newId = useId();
    const confirmId = useId();

    const rules = evaluatePolicy(newPassword, policy, context);
    const mismatch = requireConfirm && confirmPassword.length > 0 && confirmPassword !== newPassword;
    const isSelfService = autoCompleteMode === "new-password";
    // Non-"password" names in the admin modals keep Chrome's save-password
    // heuristics away from a credential that isn't the admin's.
    const newName = isSelfService ? "new-password" : "secret";
    const confirmName = isSelfService ? "confirm-password" : "secret-confirm";
    const memorable = policy.style === "memorable";

    const generate = () => {
        const generated = generatePasswordFor(policy, language, context);
        onNewPasswordChange(generated);
        onConfirmPasswordChange?.(generated);
        setShowNew(true);
        setShowConfirm(true);
        toast.info(t(memorable ? "password_generated_hint" : "password_suggested_hint"), { toastId: "password-suggested" });
    };

    return (
        <div className={`password-fields password-fields--${variant}`}>
            <div className="change-password-field">
                <span className="change-password-label-row">
                    <label htmlFor={newId}>{t(newLabelKey)}</label>
                    <span className="change-password-label-tools">
                        {showGenerator && (
                            /* Beside the field it fills. Short visible text; the accessible
                               name (and tooltip) is the full action. */
                            <button
                                type="button"
                                className="change-password-generator-btn"
                                onClick={generate}
                                disabled={disabled}
                                aria-label={t(memorable ? "password_generate" : "password_suggest")}
                                title={t(memorable ? "password_generate" : "password_suggest")}
                            >
                                {memorable ? <Dices size={12} /> : <Wand2 size={12} />}
                                {t(memorable ? "password_generate_short" : "password_suggest_short")}
                            </button>
                        )}
                        <button
                            type="button"
                            className="change-password-eye"
                            onClick={() => setShowNew(s => !s)}
                            aria-label={showNew ? t("change_password_hide") : t("change_password_show")}
                        >
                            {showNew ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                    </span>
                </span>
                <SecretInput
                    id={newId}
                    allowSave={allowSave}
                    revealed={showNew}
                    className="change-password-input"
                    name={newName}
                    value={newPassword}
                    onChange={e => onNewPasswordChange(e.target.value)}
                    disabled={disabled}
                    autoFocus={autoFocus}
                    autoComplete={autoCompleteMode}
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                />
            </div>

            {requireConfirm && (
                <div className="change-password-field">
                    <span className="change-password-label-row">
                        <label htmlFor={confirmId}>{t(confirmLabelKey)}</label>
                        <button
                            type="button"
                            className="change-password-eye"
                            onClick={() => setShowConfirm(s => !s)}
                            aria-label={showConfirm ? t("change_password_hide") : t("change_password_show")}
                        >
                            {showConfirm ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                    </span>
                    <SecretInput
                        id={confirmId}
                        allowSave={allowSave}
                        revealed={showConfirm}
                        className="change-password-input"
                        name={confirmName}
                        value={confirmPassword}
                        onChange={e => onConfirmPasswordChange?.(e.target.value)}
                        disabled={disabled}
                        autoComplete={autoCompleteMode}
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                    />
                    {mismatch && (
                        <span className="change-password-mismatch">{t("change_password_mismatch")}</span>
                    )}
                </div>
            )}

            <ul className="change-password-rules" aria-label={t("change_password_rules_label")}>
                {rules.map(r => (
                    <li key={r.key} className={`change-password-rule${r.pass ? " change-password-rule--ok" : ""}`}>
                        <span className="change-password-rule-icon">
                            {r.pass ? <Check size={14} /> : <X size={14} />}
                        </span>
                        <span>{t(r.labelKey, { min: String(policy.minLength) })}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default PasswordFields;
